/**
 * TPGodView · Track 3 · Phase R
 * ─────────────────────────────────
 * Admin/VA readonly mirror of a specific TP's portal PLUS admin-only
 * tuning knobs (badge threshold override, dispatch_mode override,
 * confidence-threshold override).
 *
 * Route: /admin/providers/:tpId/god-view
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiClient } from "../../lib/api";
import { toast } from "sonner";

export default function TPGodView() {
  const { tpId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/tpt/admin/providers/${tpId}/god-view`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load God View");
    } finally { setLoading(false); }
  }, [tpId]);

  useEffect(() => { load(); }, [load]);

  const forceModeChange = async (mode) => {
    if (!window.confirm(`Force ${data.tp.name} into ${mode.toUpperCase()} mode? This bypasses the Trust Score / tenure gate. Stamped in audit.`)) return;
    setSaving(true);
    try {
      await apiClient.patch(`/tpt/providers/${tpId}/settings`, { dispatch_mode: mode, override_gate: true });
      toast.success(`Forced ${data.tp.name} → ${mode}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  if (loading || !data) {
    return <div style={{ padding: 40, color: "#64748B" }}>Loading God View…</div>;
  }

  const { tp, counts, settings, drivers, vehicles, offers_pending, exceptions_open, audit_trail, reason_stats } = data;

  return (
    <div style={{ padding: "20px 24px", maxWidth: 1400, margin: "0 auto" }} data-testid="tp-god-view">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <Link to="/admin/providers" style={{ fontSize: 11, color: "#64748B", letterSpacing: "0.14em", textTransform: "uppercase", textDecoration: "none" }}>← Providers</Link>
          <div style={{ fontSize: 28, fontWeight: 900, color: "#0F1F17", letterSpacing: "-0.02em", marginTop: 4 }}>{tp.name} · God View</div>
          <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>tp_id · {tp.tp_id}</div>
        </div>
        {settings.autonomous_verified && (
          <div style={{ background: "linear-gradient(135deg,#F5D48C,#F59E0B)", color: "#0F1F17", padding: "8px 16px", borderRadius: 8, fontSize: 12, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            ★ Autonomous Verified
          </div>
        )}
      </div>

      {/* Counts strip */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 24 }}>
        <Tile label="Live trips" value={counts.live_trips} testId="gv-live"/>
        <Tile label="Pending offers" value={counts.pending_offers} testId="gv-offers"/>
        <Tile label="Open exceptions" value={counts.open_exceptions} tone={counts.open_exceptions > 0 ? "urgent" : "muted"} testId="gv-exceptions"/>
        <Tile label="Drivers" value={counts.drivers} testId="gv-drivers"/>
        <Tile label="Vehicles" value={counts.vehicles} testId="gv-vehicles"/>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }}>
        {/* Settings + admin knobs */}
        <div style={{ background: "#fff", border: "1px solid #E2E0D8", borderRadius: 10, padding: 18 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700, marginBottom: 12 }}>Settings & knobs</div>
          <div style={{ marginBottom: 8, fontSize: 14 }}>Dispatch mode: <b style={{ color: "#2B4C3B" }}>{settings.dispatch_mode}</b></div>
          <div style={{ marginBottom: 8, fontSize: 14 }}>Trust Score: <b>{Number(settings.trust_score ?? 0).toFixed(1)}</b></div>
          <div style={{ marginBottom: 8, fontSize: 14 }}>Autonomous reliability: <b>{settings.autonomous_reliability ?? "—"}</b></div>
          <div style={{ marginBottom: 12, fontSize: 14 }}>Confidence threshold: <b>{settings.preferences?.confidence_threshold ?? 90}</b></div>

          <div style={{ borderTop: "1px solid #E9EAE4", paddingTop: 12, marginTop: 12 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#B45309", fontWeight: 700, marginBottom: 10 }}>Admin knobs · override gate</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["manual", "assisted", "autonomous"].map(m => (
                <button key={m} disabled={saving || settings.dispatch_mode === m} onClick={() => forceModeChange(m)}
                  data-testid={`gv-force-${m}`}
                  style={{ background: settings.dispatch_mode === m ? "#94A3B8" : "#0F1F17", color: "#fff", border: "none", padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: settings.dispatch_mode === m ? "default" : "pointer" }}>
                  Force → {m}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Reason stats */}
        <div style={{ background: "#fff", border: "1px solid #E2E0D8", borderRadius: 10, padding: 18 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700, marginBottom: 12 }}>Override reasons (learning loop)</div>
          {reason_stats.length === 0 ? (
            <div style={{ fontSize: 13, color: "#64748B" }}>No overrides recorded yet</div>
          ) : (
            <div style={{ display: "grid", gap: 6 }}>
              {reason_stats.map((r, i) => (
                <div key={i} style={{ fontSize: 13, display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px dashed #E9EAE4" }}>
                  <span style={{ color: "#0F1F17", fontWeight: 700 }}>{r.reason_code}</span>
                  <span style={{ color: "#64748B" }}>{r.count}x</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fleet snapshot */}
      <div style={{ background: "#fff", border: "1px solid #E2E0D8", borderRadius: 10, padding: 18, marginBottom: 24 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700, marginBottom: 12 }}>Fleet snapshot</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
          {vehicles.slice(0, 12).map(v => (
            <div key={v.vehicle_id} style={{ background: "#F5F3EA", padding: 10, borderRadius: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 700, color: "#0F1F17" }}>{v.year || ""} {v.make} {v.model} · {v.vehicle_type}</div>
              <div style={{ color: "#64748B", fontFamily: "ui-monospace,Menlo,monospace", marginTop: 2 }}>{v.vin}</div>
              <div style={{ color: "#64748B", marginTop: 2 }}>Status: <b style={{ color: "#0F1F17" }}>{v.medexova_credentialing_status || "not_submitted"}</b></div>
            </div>
          ))}
          {vehicles.length === 0 && <div style={{ fontSize: 13, color: "#64748B" }}>No vehicles on file</div>}
        </div>
      </div>

      {/* Audit trail */}
      <div style={{ background: "#fff", border: "1px solid #E2E0D8", borderRadius: 10, padding: 18 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#64748B", fontWeight: 700, marginBottom: 12 }}>Audit trail (last 20 changes)</div>
        {audit_trail.length === 0 ? (
          <div style={{ fontSize: 13, color: "#64748B" }}>No settings changes recorded</div>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {audit_trail.map((a, i) => (
              <div key={i} style={{ fontSize: 12, padding: "6px 0", borderBottom: "1px dashed #E9EAE4" }}>
                <div style={{ display: "flex", justifyContent: "space-between", color: "#64748B" }}>
                  <span>{a.actor}{a.override_gate ? " · ⚠ override_gate" : ""}</span>
                  <span>{new Date(a.ts).toLocaleString()}</span>
                </div>
                <div style={{ color: "#0F1F17", fontFamily: "ui-monospace,Menlo,monospace", marginTop: 2 }}>{JSON.stringify(a.change)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Tile({ label, value, tone, testId }) {
  const urgent = tone === "urgent";
  return (
    <div style={{ background: urgent ? "#FEE2E2" : "#fff", border: urgent ? "1px solid #FCA5A5" : "1px solid #E2E0D8", borderRadius: 10, padding: 14 }} data-testid={testId}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: urgent ? "#B91C1C" : "#64748B", fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1, color: urgent ? "#B91C1C" : "#0F1F17" }}>{value ?? 0}</div>
    </div>
  );
}
